<!-- Bio and testimonial: a TipTap (ProseMirror) editor with a dark bar joined above it. Not
     editing, the bar reads editLabel ("Edit your bio"); editing, it becomes the toolbar: bold,
     italics, paragraph, link | undo, redo, and the character count on the right. Saving is
     the dialog's OK. -->

<template>
  <div ref="rootRef" class="bio" :class="{isEditing, canStart: !isEditing && !disabled}">
    <div class="bio-bar">
      <button
        v-if="!isEditing"
        class="bar-edit"
        type="button"
        :disabled="disabled"
        @click="$emit('start')">
        {{ editLabel }}
      </button>
      <div v-else class="toolbar" role="toolbar" aria-label="Bio formatting" @mousedown.prevent>
        <button
          v-for="tool in TOOLS"
          :key="tool.name"
          class="tool"
          :class="{isActive: tool.isActive?.(editor)}"
          type="button"
          :title="tool.title"
          :aria-label="tool.title"
          @click="tool.run(editor)">
          <component :is="tool.icon" v-if="tool.icon" class="tool-icon" />
          <span v-else :class="tool.name">{{ tool.label }}</span>
        </button>
        <span class="divider" role="separator" aria-orientation="vertical" />
        <button
          v-for="tool in HISTORY_TOOLS"
          :key="tool.name"
          class="tool"
          type="button"
          :title="tool.title"
          :aria-label="tool.title"
          :disabled="!tool.canRun(editor)"
          @click="tool.run(editor)">
          <AppModalProfileHistoryMark class="tool-icon" :is-redo="tool.name === 'redo'" />
        </button>
        <!-- rendered-text count, far right; red over maxLength (can't be saved) -->
        <span class="spacer" />
        <AppModalProfileCharCounter class="counter" :count="textLength" :max-length="maxLength" />
      </div>
    </div>
    <EditorContent ref="contentRef" :editor="editor" class="bio-content" @click="startFromClick" />
  </div>
</template>

<script setup>
import {nextTick, ref, watch} from "vue";
import {useEventListener} from "@vueuse/core";
import {EditorContent, useEditor} from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import AppModalProfileCharCounter from "@/components/App/Modal/Profile/CharCounter";
import AppModalProfileHomepageMark from "@/components/App/Modal/Profile/HomepageMark";
import AppModalProfileHistoryMark from "@/components/App/Modal/Profile/HistoryMark";

const props = defineProps({
  modelValue: {type: String, default: ""},
  isEditing: {type: Boolean, default: false},
  disabled: {type: Boolean, default: false},
  maxLength: {type: Number, default: 0},
  placeholder: {type: String, default: ""},
  editLabel: {type: String, default: "Edit"},
  safetyLimit: {type: Number, default: 0} // hard cap on typing/pasting; default 2 × maxLength
});
const emit = defineEmits(["update:modelValue", "update:textLength", "start", "cancel"]);
const textLength = ref(0);
const contentRef = ref(null);
const rootRef = ref(null);

// a click on the text block starts editing
function startFromClick() {
  if (!props.isEditing && !props.disabled) emit("start");
}

// while editing, Escape ends the edit without saving (the dialog stays open: capture phase,
// before its own Escape handler), wherever the focus is
function onEscape(event) {
  if (event.key !== "Escape" || !props.isEditing) return;
  event.preventDefault();
  event.stopPropagation();
  emit("cancel");
}

// …and so does a click in the dialog outside this control (OK and Cancel are exempt: they act
// themselves; useProfileFieldEdits.js § useDiscardOnOutside)
function onPointerDown(event) {
  if (!props.isEditing) return;
  const target = event.target;
  if (!target?.closest?.("dialog")) return;
  const isFooterAction = target.closest("[data-profile-commit], [data-profile-cancel]");
  if (rootRef.value?.contains(target) || isFooterAction) return;
  emit("cancel");
}

useEventListener(window, "keydown", onEscape, {capture: true});
useEventListener(window, "pointerdown", onPointerDown, {capture: true});

// the block scrolls; editing leaves it scrolled to the cursor, so the read view (and newly
// loaded text) would start mid-text. Show the start of the bio instead.
function scrollToStart() {
  const element = contentRef.value?.$el;
  if (element) element.scrollTop = 0;
}

function editLink(editor) {
  const previous = editor.getAttributes("link").href || "https://";
  const href = window.prompt("Link address", previous);
  if (href === null) return;
  const chain = editor.chain().focus().extendMarkRange("link");
  if (!href.trim()) return chain.unsetLink().run();
  chain.setLink({href: href.trim()}).run();
}

const TOOLS = [
  {name: "bold", label: "B", title: "Bold",
    run: (editor) => editor.chain().focus().toggleBold().run(),
    isActive: (editor) => editor?.isActive("bold")},
  {name: "italic", label: "I", title: "Italics",
    run: (editor) => editor.chain().focus().toggleItalic().run(),
    isActive: (editor) => editor?.isActive("italic")},
  {name: "paragraph", label: "¶", title: "New paragraph",
    run: (editor) => editor.chain().focus().splitBlock().run()},
  {name: "link", icon: AppModalProfileHomepageMark, title: "Link", run: editLink,
    isActive: (editor) => editor?.isActive("link")}
];
const HISTORY_TOOLS = [
  {name: "undo", title: "Undo",
    run: (editor) => editor.chain().focus().undo().run(),
    canRun: (editor) => editor?.can().undo()},
  {name: "redo", title: "Redo",
    run: (editor) => editor.chain().focus().redo().run(),
    canRun: (editor) => editor?.can().redo()}
];

function getStoredHtml(current) {
  return current.isEmpty ? "" : current.getHTML();
}

// rendered text only (ProseMirror doc.textContent), never HTML tags
function reportTextLength(current) {
  textLength.value = current.storage.characterCount.characters();
  emit("update:textLength", textLength.value);
}

// all StarterKit formats stay enabled (lists, headings…) so existing bios keep their HTML;
// only the toolbar is minimal. An empty document is stored as "".
const editor = useEditor({
  content: props.modelValue,
  editable: props.isEditing,
  extensions: [
    StarterKit,
    Link.configure({openOnClick: false, autolink: true}),
    // shown whenever the text is empty, editing or not (TipTap's default hides it when read-only)
    Placeholder.configure({placeholder: props.placeholder, showOnlyWhenEditable: false}),
    // safety cap only: typing stops there and a longer paste is cut to fit; anything under it
    // lands in full (and in the undo history). The save limit is maxLength (OK disabled).
    CharacterCount.configure({limit: props.safetyLimit || props.maxLength * 2, mode: "textSize"})
  ],
  onCreate: ({editor: current}) => reportTextLength(current),
  onTransaction: ({editor: current}) => reportTextLength(current),
  onUpdate: ({editor: current}) => emit("update:modelValue", getStoredHtml(current))
});

watch(() => props.modelValue, (html) => {
  const current = editor.value;
  if (!current || html === getStoredHtml(current)) return;
  current.commands.setContent(html || "", false); // loaded or discarded elsewhere
  scrollToStart();
});

watch(() => props.isEditing, async (isEditing) => {
  editor.value?.setEditable(isEditing);
  if (!isEditing) return scrollToStart();
  await nextTick();
  editor.value?.commands.focus("end", {scrollIntoView: false});
});
</script>

<style scoped>
/* bar + text block read as one rounded control, outlined in the bar's colour */
.bio {
  --bar-color: #555;
  --bar-height: 24px; /* bar, toolbar buttons and counter: one height, editing or not */
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--bar-color);
  border-radius: 12px;
  overflow: hidden; /* rounds the bar's top and the block's bottom corners */
}
/* centred on the toolbar line like the buttons (the row's own margin pushed it up) */
.counter.counter-row {
  display: flex;
  align-items: center;
  height: var(--bar-height);
  margin: 0;
}
.counter :deep(.counter) {
  font-size: 12px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.7);
}
.counter :deep(.counter.over) {
  color: #ff6b5b; /* red that reads on the dark toolbar */
}
/* not editing: light grey, a little lighter than the dialog, so it doesn't look like an input */
/* as tall as the card's parent lets it be (Input.vue, TestimonialEditor.vue: the dialog's height
   left); a longer text scrolls inside */
.bio-content {
  flex: 1;
  min-height: 160px; /* taller as the type got tighter: more text shows, not a shorter block */
  overflow-y: auto;
  padding: 6px 10px; /* tighter top and bottom */
  box-sizing: border-box;
  background: #ececec;
  font-size: 12px;
  line-height: 1.28;
  color: rgba(0, 0, 0, 0.75);
}
.bio.isEditing {
  --bar-color: #1c1c1c; /* editing: toolbar and outline go almost black */
  border-radius: 0; /* editing: square corners */
}
.bio.isEditing .bio-content {
  background: white;
}
.bio.canStart .bio-content {
  cursor: text;
}
.bio-content :deep(.ProseMirror) {
  min-height: 140px;
  outline: none;
}
.bio-content :deep(.ProseMirror p) {
  margin: 0 0 0.6em; /* about half a line between paragraphs */
  line-height: inherit; /* global.css sets p { line-height: 1.5rem }, a fixed 24px */
  font-size: inherit;
}
.bio-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  white-space: pre-line; /* keeps line breaks in multi-line placeholders */
  float: left;
  height: 0;
  font-weight: 300;
  font-style: italic;
  color: rgba(0, 0, 0, 0.4);
  pointer-events: none;
}
/* the joined bar, on top: "Edit your bio", or the toolbar while editing */
.bio-bar {
  display: flex;
  height: var(--bar-height);
  background: var(--bar-color); /* lighter than the old near-black, still a dark bar */
  color: white;
}
.bio-bar button {
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: none;
  color: white;
  font: inherit;
  cursor: pointer;
}
.bio-bar button:disabled {
  opacity: 0.4;
  cursor: default;
}
.bio-bar .bar-edit {
  flex: 1;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.toolbar {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 6px;
}
/* ".bio-bar .tool", not ".tool": ".bio-bar button { font: inherit }" is more specific and
   was resetting the size */
.bio-bar .tool {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: var(--bar-height);
  padding: 0 5px;
  font-size: 11px; /* B, I, ¶: same visual height as the undo/redo icons and the count */
  line-height: 1;
}
.tool.isActive {
  background: rgba(255, 255, 255, 0.2);
}
.tool .bold {
  font-weight: 800;
}
.tool .italic {
  font-style: italic;
  font-family: Georgia, serif;
}
.tool-icon {
  display: block;
  width: 13px;
  height: 13px;
  margin: 0 auto;
}
.spacer {
  flex: 1;
}
/* vertical rule between the formatting tools and undo/redo */
.divider {
  align-self: center;
  width: 1px;
  height: 18px;
  margin: 0 6px;
  background: rgba(255, 255, 255, 0.45);
}
.counter {
  padding-right: 4px;
}
.bio-bar button:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.08); /* DESIGN.md § 15 */
}
</style>
